import styled, { css, keyframes } from 'styled-components/macro';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClefVerteIcon,
  EcoLeaderIcon,
  GreenEarth,
  GreenGlobeIcon,
  Planet21Icon,
  RightArrowIcon,
  BoldArrowIcon,
  BadIcon,
  CommittedDestinationIcon,
  SoftMobilityIcon,
} from '@net0-calc/common/assets/svg';
import { copyResultsLink } from 'utils/copy-results-link';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { HOTEL_ICON } from 'common/constants/picture-paths';
import { Language } from '@net0-calc/common/types';

gsap.registerPlugin(ScrollTrigger);

type Props = {
  answerId: string | null;
};

export const TipsToReduce: FC<Props> = ({ answerId }) => {
  const { t, i18n } = useTranslation();
  const resolvedLanguage = i18n.resolvedLanguage || 'en';

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const blocksAnimationTimeLine = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        scrub: 1,
        start: 'top 900px',
        end: 'bottom bottom',
      },
    });
    blocksAnimationTimeLine
      .from(containerRef.current, {
        opacity: 0,
      })
      .to(containerRef.current, {
        opacity: 1,
        duration: 1,
      });
  }, []);

  const softMobilityLink: Record<Language, string> = {
    en: 'https://cvb.visitmonaco.com/en/environment/47026/soft-mobility-in-monaco',
    de: 'https://cvb.visitmonaco.com/de/umfeld/47026/die-sanfte-mobilitat-in-monaco',
    fr: 'https://cvb.visitmonaco.com/fr/environnement/47026/la-mobilite-douce-a-monaco',
    it: 'https://cvb.visitmonaco.com/it/ambiente/47026/la-mobilita-dolce-nel-principato',
  };

  return (
    <Root ref={containerRef}>
      <Title>
        <EmojiContainer>💡</EmojiContainer> {t('events.pages.result.tipsToReduce.title')}
      </Title>

      <StepTitleWrap>
        <BadIcon />
        <span>{t('events.pages.result.tipsToReduce.tips.involveAttendees.title')}</span>
      </StepTitleWrap>

      <StepSubtitle>
        <span
          dangerouslySetInnerHTML={{
            __html: t('events.pages.result.tipsToReduce.tips.involveAttendees.text1'),
          }}></span>{' '}
        <ButtonInTextWrapper>
          <ButtonInText
            onClick={() => {
              window.open('https://visit.netzero.mc/', '_blank');
            }}>
            <span>{t('events.pages.result.tipsToReduce.visitCalculator')}</span>
            <BoldArrowIcon />
          </ButtonInText>
        </ButtonInTextWrapper>
        <br />
        <br />
        <span
          dangerouslySetInnerHTML={{
            __html: t('events.pages.result.tipsToReduce.tips.involveAttendees.text2'),
          }}></span>{' '}
        {answerId && (
          <ButtonInTextWrapper>
            <ButtonInText
              onClick={() => {
                copyResultsLink(answerId);
              }}>
              <span>{t('events.pages.result.tipsToReduce.getResults')}</span>
              <BoldArrowIcon />
            </ButtonInText>
          </ButtonInTextWrapper>
        )}
      </StepSubtitle>

      <StepTitleWrap>
        <BadIcon />
        <span>{t('events.pages.result.tipsToReduce.tips.setOptimalTemperature.title')}</span>
      </StepTitleWrap>
      <StepSubtitle>{t('events.pages.result.tipsToReduce.tips.setOptimalTemperature.text')}</StepSubtitle>

      <StepTitleWrap>
        <BadIcon />
        <span>{t('events.pages.result.tipsToReduce.tips.manageWasteAndRecycle.title')}</span>
      </StepTitleWrap>
      <StepSubtitle>
        {t('events.pages.result.tipsToReduce.tips.manageWasteAndRecycle.text')}{' '}
        <ButtonInTextWrapper>
          <ButtonInText
            onClick={() => {
              window.open('https://smaservices-monaco.com/fr/evenements', '_blank');
            }}>
            <span> {t('events.pages.result.tipsToReduce.tips.manageWasteAndRecycle.linkText')}</span>
            <BoldArrowIcon />
          </ButtonInText>
        </ButtonInTextWrapper>
      </StepSubtitle>

      <StepTitleWrap>
        <BadIcon />
        <span>{t('events.pages.result.tipsToReduce.tips.minimisePrising.title')}</span>
      </StepTitleWrap>
      <StepSubtitle>{t('events.pages.result.tipsToReduce.tips.minimisePrising.text')}</StepSubtitle>

      <StepTitleWrap>
        <BadIcon />
        <span>{t('events.pages.result.tipsToReduce.tips.workWithLocalSuppliers.title')}</span>
      </StepTitleWrap>
      <StepSubtitle>{t('events.pages.result.tipsToReduce.tips.workWithLocalSuppliers.text')}</StepSubtitle>

      <LinksWrapper>
        <Link
          href={`https://cvb.visitmonaco.com/${resolvedLanguage}/environment/26450/a-destination-with-values`}
          target="_blank">
          <StyledCommittedDestinationIcon />
          <LinkText>{t('events.pages.result.tipsToReduce.links.guide')}</LinkText>
          <StyledRightArrowIcon />
        </Link>
        <Link
          href={`https://www.visitmonaco.com/${resolvedLanguage}/environment/37823/responsible-tourism-white-paper`}
          target="_blank">
          <StyledGreenEarth fill="var(--color2)" />
          <LinkText>{t('events.pages.result.tipsToReduce.links.whitepapert')}</LinkText>
          <StyledRightArrowIcon />
        </Link>
        <Link href={softMobilityLink[resolvedLanguage as Language]} target="_blank">
          <StyledSoftMobilityIcon />
          <LinkText>{t('events.pages.result.tipsToReduce.links.hotels')}</LinkText>
          <StyledRightArrowIcon />
        </Link>
      </LinksWrapper>
      <HotelsWrapper>
        <Title>
          <EmojiContainer>
            <HotelIcon src={HOTEL_ICON} />
          </EmojiContainer>
          {t('personal.pages.result.tipsToReduce.hotelsTitle')}
        </Title>
        <Paragraph>
          {t('events.pages.result.tipsToReduce.hotelParagraph')}
          <ParagraphLink
            target="_blank"
            href={`https://www.Visitmonaco.Com/${resolvedLanguage}/environnement/26532/une-hotellerie-engagee-pour-un-sejour-responsable`}>
            {' ' + t('events.pages.result.tipsToReduce.hotelParagraphLink')}
          </ParagraphLink>
        </Paragraph>
        <HotelsGrid>
          <>
            <HotelsContainer>
              Hotel de Paris
              <IconsWrapper>
                <GreenGlobeIcon />
              </IconsWrapper>
            </HotelsContainer>
            <HotelsContainer>
              Hotel Métropole Monte-Carlo
              <IconsWrapper>
                <ClefVerteIcon />
                <EcoLeaderIcon />
              </IconsWrapper>
            </HotelsContainer>
            <HotelsContainer>
              Hotel Hermitage Monte-Carlo
              <IconsWrapper>
                <GreenGlobeIcon />
              </IconsWrapper>
            </HotelsContainer>
            <HotelsContainer>
              Hotel Monte-Carlo Beach
              <IconsWrapper>
                <GreenGlobeIcon />
              </IconsWrapper>
            </HotelsContainer>
            <HotelsContainer>
              Hotel Fairmont Monte Carlo
              <IconsWrapper>
                <Planet21Icon />
              </IconsWrapper>
            </HotelsContainer>
          </>
          <>
            <HotelsContainer>
              Méridien Beach Plaza
              <IconsWrapper>
                <ClefVerteIcon />
                <EcoLeaderIcon />
              </IconsWrapper>
            </HotelsContainer>
            <HotelsContainer>
              Monte-Carlo Bay Hotel & Resort
              <IconsWrapper>
                <GreenGlobeIcon />
                <EcoLeaderIcon />
              </IconsWrapper>
            </HotelsContainer>
            <HotelsContainer>
              Hotel Columbus
              <IconsWrapper>
                <EcoLeaderIcon />
              </IconsWrapper>
            </HotelsContainer>
            <HotelsContainer>
              Hotel Novotel Monte-Carlo
              <IconsWrapper>
                <Planet21Icon />
              </IconsWrapper>
            </HotelsContainer>
          </>
        </HotelsGrid>
      </HotelsWrapper>
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  max-width: 845px;
  margin-bottom: 40px;

  @media ${DEVICE.laptop} {
    margin-bottom: 60px;
  }
`;

const Title = styled.div`
  ${TYPOGRAPHY.bodyMedium20}
  color: var(--color6);
  margin-bottom: 20px;
  display: flex;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.titleSemibold32}
    margin-bottom: 32px;
  }
`;

const Paragraph = styled.div`
  $${TYPOGRAPHY.boydRegular20}
  color: var(--color6);
  width: 100%;
  margin-bottom: 24px;
`;

const LinksWrapper = styled.div`
  max-width: 1117px;
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;

  @media ${DEVICE.laptop} {
    flex-direction: row;
    margin-bottom: 60px;
  }
`;

const HotelsWrapper = styled.div`
  max-width: 744px;
  margin-top: 30px;

  @media ${DEVICE.laptop} {
    margin-top: 60px;
  }
`;

const HotelsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px 40px;

  @media ${DEVICE.laptop} {
    min-width: 1036px;
    grid-template-columns: repeat(3, 1fr);
  }
`;

const HotelsContainer = styled.div`
  ${TYPOGRAPHY.bodyMedium16}
  font-size: 16px;
  line-height: 20px;
  color: var(--color1);

  display: flex;
  gap: 16px;
  align-items: center;
`;

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Link = styled.a`
  ${TYPOGRAPHY.bodyMedium20}
  padding: 12px;
  display: flex;
  align-items: center;
  border-radius: 15px;
  border: 2px solid var(--color14);
  color: var(--color2);
  text-decoration: none;
  transition: 0.3s;
  width: 343px;

  &:not(:last-child) {
    margin: 0 0 12px 0;
  }

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyMedium16}

    width: fit-content;
    padding: 20px;

    &:not(:last-child) {
      margin: 0 20px 0 0;
    }
  }

  &:hover {
    border: 2px solid var(--color2);
  }
`;

const LinkText = styled.p`
  flex: 1;
  width: 100%;

  @media ${DEVICE.laptop} {
    white-space: nowrap;
  }
`;

const iconsCommonCSS = css`
  margin-right: 16px;
`;

const StyledCommittedDestinationIcon = styled(CommittedDestinationIcon)`
  ${iconsCommonCSS}
`;

const StyledSoftMobilityIcon = styled(SoftMobilityIcon)`
  ${iconsCommonCSS}
`;

const StyledGreenEarth = styled(GreenEarth)`
  ${iconsCommonCSS}
`;

const StyledRightArrowIcon = styled(RightArrowIcon)`
  margin-left: 16px;
  stroke: var(--color2);
`;

const emojiPulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
`;

const EmojiContainer = styled.div`
  ${TYPOGRAPHY.bodyMedium20}
  cursor: pointer;
  margin-right: 24px;
  &:hover {
    animation: ${emojiPulseAnimation} 0.2s ease-in normal;
  }

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.titleSemibold32}
    margin-right: 16px;
  }
`;

const StepTitleWrap = styled.div`
  $${TYPOGRAPHY.boydRegular20}
  color: var(--color6);
  display: flex;
  align-items: center;
  & svg {
    display: block;
    margin-right: 12px;
    flex-shrink: 0;
    width: 26px;
  }

  &:not(:last-child) {
    margin-bottom: 13px;
  }
`;
const StepSubtitle = styled.span`
  ${TYPOGRAPHY.bodyRegular16}
  position: relative;
  display: inline-block;
  padding-left: 38px;
  margin-bottom: 16px;
  color: var(--color9);
  &:nth-last-of-type(1) {
    margin-bottom: 20px;
  }
  @media ${DEVICE.mobileL} {
    margin-bottom: 30px;
    &:nth-last-of-type(1) {
      margin-bottom: 32px;
    }
  }
`;
const ButtonInTextWrapper = styled.span`
  display: inline-block;
`;

const ButtonInText = styled.div`
  ${TYPOGRAPHY.captionMedium12}
  width: auto;
  height: 26px;
  padding: 4px 8px 4px 8px;
  display: inline-flex;
  align-items: center;
  column-gap: 6px;
  border-radius: 8px;
  color: var(--color2);
  border: 2px solid rgba(197, 235, 197, 1);
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    border: 2px solid var(--color2);
  }
`;

const ParagraphLink = styled.a`
  text-decoration: none;
  color: var(--color2);
`;

const HotelIcon = styled.img`
  width: 30px;
`;
